import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../services/api';

const PortfolioContext = createContext(null);

// Step definitions
export const STEPS = [
  { path: '/select', label: 'Select Funds', number: 1 },
  { path: '/optimize', label: 'Configure', number: 2 },
  { path: '/results', label: 'Results', number: 3 },
  { path: '/backtest', label: 'Backtest', number: 4 },
];

// Helper to get step number from path
export const getStepFromPath = (path) => {
  const step = STEPS.find(s => s.path === path);
  return step ? step.number : 1;
};

// Helper to get path from step number
export const getPathFromStep = (stepNumber) => {
  const step = STEPS.find(s => s.number === stepNumber);
  return step ? step.path : '/select';
};

export const usePortfolio = () => {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error('usePortfolio must be used within a PortfolioProvider');
  }
  return context;
};

// Storage keys
const STORAGE_KEYS = {
  selectedFunds: 'allocq_selectedFunds',
  dateRange: 'allocq_dateRange',
  optimizationParams: 'allocq_optimizationParams',
  optimizationResults: 'allocq_optimizationResults',
  navData: 'allocq_navData',
  scannedFunds: 'allocq_scannedFunds',
  completedSteps: 'allocq_completedSteps',
};

// Helper to safely parse JSON from localStorage
const safeParseJSON = (key, defaultValue) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch {
    return defaultValue;
  }
};

export const PortfolioProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Core state
  const [selectedFunds, setSelectedFundsState] = useState(() =>
    safeParseJSON(STORAGE_KEYS.selectedFunds, [])
  );

  const [dateRange, setDateRangeState] = useState(() =>
    safeParseJSON(STORAGE_KEYS.dateRange, { startDate: null, endDate: null })
  );

  const [optimizationParams, setOptimizationParamsState] = useState(() =>
    safeParseJSON(STORAGE_KEYS.optimizationParams, {
      objective: 'sharpe',
      constraints: {
        max_allocation: 0.4,
        min_allocation: 0.0
      }
    })
  );

  const [optimizationResults, setOptimizationResultsState] = useState(() =>
    safeParseJSON(STORAGE_KEYS.optimizationResults, null)
  );

  const [navData, setNavDataState] = useState(() =>
    safeParseJSON(STORAGE_KEYS.navData, null)
  );

  const [scannedFunds, setScannedFundsState] = useState(() =>
    safeParseJSON(STORAGE_KEYS.scannedFunds, [])
  );

  const [scanResults, setScanResults] = useState(null);

  // Track completed steps
  const [completedSteps, setCompletedStepsState] = useState(() =>
    safeParseJSON(STORAGE_KEYS.completedSteps, [])
  );

  // Loading state
  const [loading, setLoading] = useState(false);

  // Rate limit error state
  const [rateLimitError, setRateLimitError] = useState(null);

  // Current step based on URL
  const currentStep = getStepFromPath(location.pathname);

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.selectedFunds, JSON.stringify(selectedFunds));
  }, [selectedFunds]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.dateRange, JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.optimizationParams, JSON.stringify(optimizationParams));
  }, [optimizationParams]);

  useEffect(() => {
    if (optimizationResults) {
      localStorage.setItem(STORAGE_KEYS.optimizationResults, JSON.stringify(optimizationResults));
    }
  }, [optimizationResults]);

  useEffect(() => {
    if (navData) {
      localStorage.setItem(STORAGE_KEYS.navData, JSON.stringify(navData));
    }
  }, [navData]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.scannedFunds, JSON.stringify(scannedFunds));
  }, [scannedFunds]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.completedSteps, JSON.stringify(completedSteps));
  }, [completedSteps]);

  // State setters with persistence
  const setSelectedFunds = useCallback((fundsOrUpdater) => {
    setSelectedFundsState(prev => {
      const newFunds = typeof fundsOrUpdater === 'function' ? fundsOrUpdater(prev) : fundsOrUpdater;
      return newFunds;
    });
  }, []);

  const setDateRange = useCallback((range) => {
    setDateRangeState(range);
  }, []);

  const setOptimizationParams = useCallback((paramsOrUpdater) => {
    setOptimizationParamsState(prev => {
      return typeof paramsOrUpdater === 'function' ? paramsOrUpdater(prev) : paramsOrUpdater;
    });
  }, []);

  const setOptimizationResults = useCallback((results) => {
    setOptimizationResultsState(results);
  }, []);

  const setNavData = useCallback((data) => {
    setNavDataState(data);
  }, []);

  const setScannedFunds = useCallback((funds) => {
    setScannedFundsState(funds);
  }, []);

  // Mark step as completed
  const markStepCompleted = useCallback((stepNumber) => {
    setCompletedStepsState(prev => {
      if (!prev.includes(stepNumber)) {
        return [...prev, stepNumber];
      }
      return prev;
    });
  }, []);

  // Check if step is accessible
  const canAccessStep = useCallback((stepNumber) => {
    if (stepNumber === 1) return true;
    if (stepNumber === 2) return selectedFunds.length >= 2 && navData;
    if (stepNumber === 3) return optimizationResults !== null;
    if (stepNumber === 4) return optimizationResults !== null;
    return false;
  }, [selectedFunds.length, navData, optimizationResults]);

  // Navigation functions
  const goToStep = useCallback((stepNumber) => {
    if (canAccessStep(stepNumber)) {
      const path = getPathFromStep(stepNumber);
      navigate(path);
    } else {
      toast.warning('Please complete the previous steps first');
    }
  }, [canAccessStep, navigate]);

  const nextStep = useCallback(() => {
    const next = currentStep + 1;
    if (next <= 4) {
      goToStep(next);
    }
  }, [currentStep, goToStep]);

  const prevStep = useCallback(() => {
    const prev = currentStep - 1;
    if (prev >= 1) {
      goToStep(prev);
    }
  }, [currentStep, goToStep]);

  // Fetch NAV data
  const fetchNavData = useCallback(async () => {
    if (selectedFunds.length < 2) return;

    try {
      setLoading(true);
      const fundCodes = selectedFunds.map(f => f.schemeCode).join(',');
      const response = await api.getNavData(fundCodes, false);
      setNavData(response.data);
      toast.success('NAV data loaded successfully!');
    } catch (error) {
      let errorMsg = 'Failed to fetch NAV data: ';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg += 'Request timed out. Please check your connection and try again.';
      } else {
        errorMsg += (error.response?.data?.detail || error.message);
      }
      toast.error(errorMsg);
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [selectedFunds, setNavData]);

  // Auto-fetch NAV data when funds change
  useEffect(() => {
    if (selectedFunds.length >= 2) {
      fetchNavData();
    }
  }, [selectedFunds.length]); // Only trigger on count change to avoid loops

  // Adjust constraints when number of funds changes
  useEffect(() => {
    if (selectedFunds.length >= 2) {
      const minRequired = 1.0 / selectedFunds.length;
      if (optimizationParams.constraints.max_allocation < minRequired) {
        const newMaxAlloc = Math.ceil(minRequired * 20) / 20;
        setOptimizationParams(prev => ({
          ...prev,
          constraints: {
            ...prev.constraints,
            max_allocation: newMaxAlloc
          }
        }));
        toast.info(`Max allocation adjusted to ${(newMaxAlloc * 100).toFixed(0)}% for ${selectedFunds.length} funds`, {
          autoClose: 3000
        });
      }
    }
  }, [selectedFunds.length, optimizationParams.constraints.max_allocation, setOptimizationParams]);

  // Optimization function
  const handleOptimize = useCallback(async () => {
    if (selectedFunds.length < 2) {
      toast.error('Please select at least 2 funds');
      return;
    }

    try {
      setLoading(true);

      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const response = await api.optimizePortfolio({
        scheme_codes: selectedFunds.map(f => f.schemeCode),
        objective: optimizationParams.objective,
        start_date: formatDate(dateRange.startDate),
        end_date: formatDate(dateRange.endDate),
        constraints: optimizationParams.constraints
      });

      setOptimizationResults(response.data);
      markStepCompleted(2);
      toast.success('Portfolio optimized successfully!');
      navigate('/results');
    } catch (error) {
      // Handle rate limit (429) error specially
      if (error.response?.status === 429) {
        const detail = error.response.data?.detail;
        const limit = detail?.limit || 20;
        const used = detail?.used || limit;
        setRateLimitError({
          message: `Daily limit of ${limit} optimizations reached. Please try again tomorrow.`,
          limit,
          used,
          remaining: 0
        });
        return; // Don't show toast, show the universe error screen instead
      }

      let errorMsg = 'Optimization failed: ';
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg += 'Request timed out. Please try with fewer funds or a shorter date range.';
      } else if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMsg += detail.map(err => {
            if (typeof err === 'string') return err;
            if (err.msg) return err.msg;
            if (err.message) return err.message;
            return JSON.stringify(err);
          }).join('; ');
        } else if (typeof detail === 'string') {
          errorMsg += detail;
        } else if (typeof detail === 'object') {
          errorMsg += detail.error || detail.msg || detail.message || JSON.stringify(detail);
        } else {
          errorMsg += String(detail);
        }
      } else if (error.message) {
        errorMsg += error.message;
      } else {
        errorMsg += 'Unknown error occurred';
      }
      toast.error(errorMsg);
      console.error('Optimization error:', error.response?.data || error);
    } finally {
      setLoading(false);
    }
  }, [selectedFunds, optimizationParams, dateRange, setOptimizationResults, markStepCompleted, navigate]);

  // Handle constraint changes
  const handleConstraintChange = useCallback((key, value) => {
    setOptimizationParams(prev => ({
      ...prev,
      constraints: {
        ...prev.constraints,
        [key]: value
      }
    }));
  }, [setOptimizationParams]);

  // Scanner handlers
  const handleScanComplete = useCallback((results) => {
    console.log('Scan complete:', results);
    setScanResults(results);

    const matched = results.matched_funds || [];
    const fundsToAddMap = new Map(); // Use Map to deduplicate by schemeCode

    matched.forEach(item => {
      if (item.matches && item.matches.length > 0) {
        const bestMatch = item.matches[0];
        if (bestMatch.similarity >= 0.75) {
          const schemeCode = String(bestMatch.scheme_code);
          // Only add if not already in the map (deduplicates scan results)
          if (!fundsToAddMap.has(schemeCode)) {
            fundsToAddMap.set(schemeCode, {
              schemeCode: schemeCode,
              schemeName: bestMatch.scheme_name,
              // Required for react-select to display properly
              value: schemeCode,
              label: bestMatch.scheme_name,
              assetType: 'mutual_fund'
            });
          }
        }
      }
    });

    const fundsToAdd = Array.from(fundsToAddMap.values());

    if (fundsToAdd.length > 0) {
      setSelectedFunds(prevFunds => {
        const existingCodes = new Set(prevFunds.map(f => f.schemeCode));
        const newFunds = fundsToAdd.filter(f => !existingCodes.has(f.schemeCode));
        if (newFunds.length > 0) {
          toast.success(`Added ${newFunds.length} funds from scan!`, {
            autoClose: 3000
          });
        }
        return [...prevFunds, ...newFunds];
      });
    }

    setScannedFunds(matched);
  }, [setSelectedFunds, setScannedFunds]);

  const handleScanError = useCallback((error) => {
    console.error('Scan error:', error);
    toast.error('Scan failed: ' + error.message, {
      autoClose: 5000
    });
  }, []);

  // Clear rate limit error
  const clearRateLimitError = useCallback(() => {
    setRateLimitError(null);
  }, []);

  // Reset/Start Over
  const startOver = useCallback(() => {
    setSelectedFunds([]);
    setDateRange({ startDate: null, endDate: null });
    setOptimizationResults(null);
    setNavData(null);
    setScannedFunds([]);
    setScanResults(null);
    setCompletedStepsState([]);

    // Clear localStorage
    Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));

    navigate('/select');
    toast.info('Starting fresh!');
  }, [setSelectedFunds, setDateRange, setOptimizationResults, setNavData, setScannedFunds, navigate]);

  // Route guard - redirect if trying to access step without prerequisites
  useEffect(() => {
    const step = getStepFromPath(location.pathname);
    if (step > 1 && !canAccessStep(step)) {
      // Find the last accessible step
      for (let i = step - 1; i >= 1; i--) {
        if (canAccessStep(i)) {
          navigate(getPathFromStep(i), { replace: true });
          return;
        }
      }
      navigate('/select', { replace: true });
    }
  }, [location.pathname, canAccessStep, navigate]);

  const value = {
    // State
    selectedFunds,
    dateRange,
    optimizationParams,
    optimizationResults,
    navData,
    scannedFunds,
    scanResults,
    completedSteps,
    loading,
    currentStep,
    rateLimitError,

    // Setters
    setSelectedFunds,
    setDateRange,
    setOptimizationParams,
    setOptimizationResults,
    setNavData,
    setScannedFunds,

    // Actions
    fetchNavData,
    handleOptimize,
    handleConstraintChange,
    handleScanComplete,
    handleScanError,
    startOver,
    clearRateLimitError,

    // Navigation
    goToStep,
    nextStep,
    prevStep,
    canAccessStep,
    markStepCompleted,

    // Constants
    STEPS,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
};

export default PortfolioContext;
